import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import AboutNav from '../../Components/Navigation/AboutNav';
import SEBISlider from '../../Components/Common/SEBISlider';
import './News_page.css';

const News_page = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');

  // SEO Meta Data
  const seoData = {
            title: 'Stock Market News & Updates | Trillion Stock Research',
    description: 'Get the latest stock market news, Indian market updates, and financial analysis. Stay informed with real-time market trends, Sensex, Nifty, and global market insights.',
    keywords: 'stock market news, Indian stock market, Sensex, Nifty, BSE, NSE, market analysis, financial news, stock market updates, market trends, investment news'
  };

  const categories = [
    { id: 'all', name: 'All News' },
    { id: 'indian', name: 'Indian Markets' },
    { id: 'us', name: 'US Markets' },
    { id: 'policy', name: 'Policy Updates' },
    { id: 'analysis', name: 'Market Analysis' }
  ];

  // SEO-optimized content sections
  const seoSections = [
    {
      title: "Indian Stock Market News",
      content: "Stay updated with the latest developments in the Indian stock market, including Sensex and Nifty movements, BSE and NSE updates, and comprehensive market analysis."
    },
    {
      title: "Global Market Updates",
      content: "Get insights into global market trends, US stock market performance, and international market analysis to make informed investment decisions."
    },
    {
      title: "Financial Policy Updates",
      content: "Keep track of important financial policy changes, RBI announcements, and regulatory updates that impact the stock market and investment landscape."
    }
  ];

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch Indian market headlines
        const indiaResponse = await fetch(
          'https://gnews.io/api/v4/top-headlines?category=business&country=in&max=5&apikey=dba650bd7b39ee03dfe393405503dfb9'
        );
        
        // Fetch US market headlines
        const usResponse = await fetch(
          'https://gnews.io/api/v4/top-headlines?category=business&country=us&max=5&apikey=dba650bd7b39ee03dfe393405503dfb9'
        );
        
        if (!indiaResponse.ok || !usResponse.ok) {
          throw new Error('Failed to fetch news');
        }

        const indiaData = await indiaResponse.json();
        const usData = await usResponse.json();
        
        // Combine and format news from both sources
        const formattedIndiaNews = indiaData.articles.map((article, index) => ({
          id: `india-${index + 1}`,
          title: article.title,
          description: article.description,
          content: article.content,
          category: 'indian',
          source: article.source.name,
          author: article.author,
          time: formatTime(article.publishedAt),
          url: article.url,
          imageUrl: article.image
        }));

        const formattedUsNews = usData.articles.map((article, index) => ({
          id: `us-${index + 1}`,
          title: article.title,
          description: article.description,
          content: article.content,
          category: 'us',
          source: article.source.name,
          author: article.author,
          time: formatTime(article.publishedAt),
          url: article.url,
          imageUrl: article.image
        }));

        // Combine both news arrays
        const allNews = [...formattedIndiaNews, ...formattedUsNews];
        setNewsItems(allNews);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const handleSearch = useCallback((value) => {
    try {
      setSearchQuery(value);
      setSearchError('');
    } catch (err) {
      setSearchError('Error processing search query');
      console.error('Search error:', err);
    }
  }, []);

  const filteredNews = newsItems.filter(news => {
    try {
      const matchesCategory = selectedCategory === 'all' || news.category === selectedCategory;
      const matchesSearch = searchQuery === '' || 
        news.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        news.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    } catch (err) {
      console.error('Filter error:', err);
      return true;
    }
  });

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <meta property="og:title" content="Social Media Title" />
          <meta property="og:description" content="Social Media Description" />
          <link rel="canonical" href="https://example.com/my-page" />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "My Page"
            })}
          </script>
        </Helmet>
        <AboutNav />
        <SEBISlider />
        <div className="trillion-news-page">
          <div className="trillion-news-page-container">
            <div className="trillion-news-page-header">
              <h1>Market News</h1>
              <p>Loading latest market updates...</p>
            </div>
            <div className="trillion-news-page-content">
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <div key={index} className="trillion-news-card loading">
                  <div className="trillion-news-card-image loading-pulse"></div>
                  <div className="trillion-news-card-content">
                    <div className="trillion-news-card-category loading-pulse"></div>
                    <div className="trillion-news-card-title loading-pulse" aria-hidden="true" />
                    <p className="trillion-news-card-description loading-pulse"></p>
                    <div className="trillion-news-card-meta">
                      <span className="trillion-news-card-source loading-pulse"></span>
                      <span className="trillion-news-card-time loading-pulse"></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <meta property="og:title" content="Social Media Title" />
          <meta property="og:description" content="Social Media Description" />
          <link rel="canonical" href="https://example.com/my-page" />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "My Page"
            })}
          </script>
        </Helmet>
        <AboutNav />
        <SEBISlider />
        <div className="trillion-news-page">
          <div className="trillion-news-page-container">
            <div className="trillion-news-page-header">
              <h1>Market News</h1>
              <p className="error-message">Unable to load news updates. Please try again later.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta property="og:title" content="Social Media Title" />
        <meta property="og:description" content="Social Media Description" />
        <link rel="canonical" href="https://example.com/my-page" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "My Page"
          })}
        </script>
      </Helmet>
      <AboutNav />
      <SEBISlider />
      <div className="trillion-news-page">
        <div className="trillion-news-page-container">
          <div className="trillion-news-page-header">
            <h1>Stock Market News & Updates</h1>
            <p className="seo-description">Stay informed with the latest stock market news, Indian market updates, and comprehensive financial analysis. Get real-time insights into Sensex, Nifty, and global market trends.</p>
          </div>

          {/* SEO Content Sections */}
          <div className="trillion-news-seo-sections">
            {seoSections.map((section, index) => (
              <div key={index} className="trillion-news-seo-section">
                <h2>{section.title}</h2>
                <p>{section.content}</p>
              </div>
            ))}
          </div>

          <div className="trillion-news-page-filters">
            <div className="trillion-news-categories">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`trillion-news-category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
            <div className="trillion-news-search">
              <input
                type="text"
                placeholder="Search news..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className={searchError ? 'error' : ''}
              />
              {searchError && <span className="search-error">{searchError}</span>}
            </div>
          </div>

          <div className="trillion-news-page-content">
            {filteredNews.length > 0 ? (
              filteredNews.map(news => (
                <div key={news.id} className="trillion-news-card">
                  {news.imageUrl && (
                    <div className="trillion-news-card-image">
                      <img src={news.imageUrl} alt={news.title} />
                    </div>
                  )}
                  <div className="trillion-news-card-content">
                    <div className="trillion-news-card-category">{categories.find(c => c.id === news.category)?.name}</div>
                    <h3 className="trillion-news-card-title">{news.title}</h3>
                    <p className="trillion-news-card-description">{news.description}</p>
                    <div className="trillion-news-card-meta">
                      <span className="trillion-news-card-source">{news.source}</span>
                      <span className="trillion-news-card-time">{news.time}</span>
                    </div>
                    <a 
                      href={news.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="trillion-news-read-more"
                    >
                      Read Full Story
                      <svg className="trillion-news-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="trillion-news-no-results">
                <p>No news found for the selected category. Try a different category or search term.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default News_page;
